import { useState } from 'react';
import { importRates } from '../../../services/fxRateService';
import { useAuth } from '../../../hooks/useAuth';
import FormField from '../../shared/Forms/FormField';
import SelectField from '../../shared/Forms/SelectField';
import Button from '../../shared/UI/Button';

/**
 * Bulk import of year-end rates.
 *
 * This is the same importRates() path the future vendor feed will use, so the
 * report it renders is what a maintainer will read when the real multi-year
 * seed is loaded. Accepted and rejected rows are shown SIDE BY SIDE and every
 * rejection carries its reason — a silent partial load is the failure mode
 * worth designing against here.
 */

const SAMPLE = `GHS,2022,0.099
GHS,2023,0.083
NGN,2023,0.0011`;

/**
 * Parse pasted CSV/TSV into { currency, year, rate } rows. Tolerant of a header
 * line, blank lines, and comma/tab/semicolon separators. Malformed lines are
 * returned as parse errors rather than silently skipped.
 */
export function parseRows(text) {
  const rows = [];
  const parseErrors = [];
  // Tracked separately from the line index so a leading blank line does not
  // push a header row through as data.
  let seenContent = false;

  String(text || '').split(/\r?\n/).forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return;

    const parts = trimmed.split(/[,;\t]/).map(p => p.trim());
    // Skip a header row, identified by a non-numeric year column.
    const isFirstContentLine = !seenContent;
    seenContent = true;
    if (isFirstContentLine && parts[1] && isNaN(Number(parts[1]))) return;

    if (parts.length < 3) {
      parseErrors.push({ line: i + 1, text: trimmed, reason: 'Expected 3 columns: currency, year, rate' });
      return;
    }
    rows.push({ currency: parts[0], year: parts[1], rate: parts[2], note: parts[3] || '' });
  });

  return { rows, parseErrors };
}

export default function FXRateImport({ onDone, onCancel }) {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [defaultStatus, setDefaultStatus] = useState('provisional');
  const [allowCorrections, setAllowCorrections] = useState(false);
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);
  const [parseErrors, setParseErrors] = useState([]);
  const [error, setError] = useState('');

  function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.readAsText(file);
  }

  async function run() {
    setError('');
    const { rows, parseErrors: pe } = parseRows(text);
    setParseErrors(pe);

    if (!rows.length) {
      setError('No rows to import. Paste rows as currency,year,rate — one per line.');
      return;
    }
    if (allowCorrections && !reason.trim()) {
      setError('A reason is required when superseding closed years.');
      return;
    }

    setRunning(true);
    try {
      setReport(await importRates(rows, {
        performedBy: user.uid,
        defaultStatus,
        allowCorrections,
        reason,
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  }

  const s = report?.summary;

  return (
    <div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="alert alert-info">
        One row per line: <code>currency,year,rate</code> (an optional 4th column is a note).
        The rate is <strong>USD per 1 unit</strong> of the currency — GHS ≈ 0.083, not 12.
        Rows are checked individually: a bad row is reported and skipped, it does not fail the batch.
      </div>

      <div className="form-grid mb-4">
        <SelectField
          label="Status for imported rows"
          value={defaultStatus}
          onChange={e => setDefaultStatus(e.target.value)}
          placeholder=""
          hint="Applied to rows that do not carry their own status."
          options={[
            { value: 'provisional', label: 'Provisional — year not yet closed' },
            { value: 'closed', label: 'Closed — final, locks each year' },
          ]}
        />
        <FormField label="Closed years"
          hint="Off by default. When on, a closed year can be superseded — the previous value is archived, not lost.">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={allowCorrections}
              onChange={e => setAllowCorrections(e.target.checked)} />
            <span style={{ fontSize: 14 }}>Allow corrections to closed years</span>
          </label>
        </FormField>
        {allowCorrections && (
          <FormField label="Reason for corrections" required
            hint="Applied to every corrected row; recorded in history and the audit log.">
            <input className="form-input" value={reason}
              placeholder="e.g. Vendor restatement of December closes"
              onChange={e => setReason(e.target.value)} />
          </FormField>
        )}
      </div>

      <FormField label="Rows" hint="Paste below, or load a .csv / .txt file.">
        <textarea
          className="form-input"
          rows={10}
          style={{ fontFamily: 'monospace', fontSize: 13 }}
          value={text}
          placeholder={SAMPLE}
          onChange={e => setText(e.target.value)}
        />
      </FormField>

      <div className="flex gap-3 mb-4" style={{ alignItems: 'center' }}>
        <input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={handleFile} />
        <Button size="sm" variant="ghost" type="button" onClick={() => setText(SAMPLE)}>
          Insert sample rows
        </Button>
      </div>

      {parseErrors.length > 0 && (
        <div className="alert alert-warning">
          <strong>{parseErrors.length} line(s) could not be parsed and were not imported:</strong>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {parseErrors.map(p => (
              <li key={p.line}>Line {p.line}: {p.reason} — <code>{p.text}</code></li>
            ))}
          </ul>
        </div>
      )}

      {report && (
        <div className="card mb-4">
          <div className="card-header">
            <h3>Import report</h3>
            <div className="flex gap-3">
              <span className="badge badge-success">{s.accepted} accepted</span>
              <span className={`badge ${s.rejected ? 'badge-danger' : 'badge-default'}`}>
                {s.rejected} rejected
              </span>
              <span className={`badge ${s.warnings ? 'badge-warning' : 'badge-default'}`}>
                {s.warnings} warning{s.warnings === 1 ? '' : 's'}
              </span>
            </div>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <p className="form-label">Accepted ({report.accepted.length})</p>
                {report.accepted.length === 0
                  ? <p className="form-hint">None.</p>
                  : (
                    <ul style={{ margin: '4px 0 0 18px', fontSize: 13 }}>
                      {report.accepted.map(a => (
                        <li key={a.index}>
                          Row {a.index + 1}: <strong>{a.currency} {a.year}</strong> = {a.rate} ({a.status})
                          {a.warning && (
                            <div style={{ color: 'var(--color-warning)', fontSize: 12 }}>⚠ {a.warning}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
              <div>
                <p className="form-label">Rejected ({report.rejected.length})</p>
                {report.rejected.length === 0
                  ? <p className="form-hint">None.</p>
                  : (
                    <ul style={{ margin: '4px 0 0 18px', fontSize: 13 }}>
                      {report.rejected.map(r => (
                        <li key={r.index} style={{ marginBottom: 6 }}>
                          Row {r.index + 1}:{' '}
                          <code>{r.row?.currency},{r.row?.year},{r.row?.rate}</code>
                          <div style={{ color: 'var(--color-error)', fontSize: 12 }}>{r.reason}</div>
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            </div>
            {s.rejected > 0 && (
              <p className="form-hint" style={{ marginTop: 12 }}>
                Rejected rows were not written. Correct them and re-run — rows that already
                imported will simply be rewritten with the same values.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex-between">
        <Button variant="secondary" type="button" onClick={onCancel}>
          {report ? 'Close' : 'Cancel'}
        </Button>
        <div className="flex gap-3">
          {report && (
            <Button variant="primary" type="button" onClick={onDone}>
              Done — back to rates
            </Button>
          )}
          <Button variant={report ? 'secondary' : 'primary'} type="button"
            loading={running} onClick={run}>
            {report ? 'Re-run import' : 'Import rows'}
          </Button>
        </div>
      </div>
    </div>
  );
}
